import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';

/**
 * Keyboard shortcuts with a registry that feeds the shortcut-help overlay (press `?`).
 *
 *   useHotkeys([
 *     { keys: 'f2', description: 'Focus search', group: 'POS', handler: () => searchRef.current?.focus() },
 *     { keys: 'alt+1', description: 'Go to POS', handler: goPos },
 *     { keys: 'ctrl+enter', description: 'Pay', handler: pay, allowInInputs: true },
 *   ], { enabled: !modalOpen });
 *
 * Combo syntax: modifiers `ctrl|alt|shift|meta` joined with `+`, then a key name (`f1`..`f12`, `enter`, `escape`,
 * `/`, `?`, `1`, `a`...). `mod` = ctrl on Windows/Linux and meta on macOS.
 *
 * Input handling: while focus is in an input/textarea/select/contenteditable, bindings fire only if
 * `allowInInputs` is true. Default for `allowInInputs` is TRUE for F-keys and for combos with ctrl/alt/meta
 * (those cannot be typed text) and FALSE for plain keys / shift+key.
 */

export interface HotkeyBinding {
  keys: string;
  handler: (event: KeyboardEvent) => void;
  /** Shown in the help overlay. Bindings without a description are not listed. */
  description?: string;
  group?: string;
  allowInInputs?: boolean;
  /** Default true. */
  preventDefault?: boolean;
}

export interface HotkeyOptions {
  enabled?: boolean;
}

interface ParsedCombo {
  key: string;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  meta: boolean;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || '');

export function parseCombo(combo: string): ParsedCombo {
  const parts = combo
    .toLowerCase()
    .split('+')
    .map((p) => p.trim())
    .filter(Boolean);
  // A literal "+" key is written as "shift+=" or "plus"; a trailing empty part means "+".
  const key = parts.pop() ?? '';
  const mods = new Set(parts);
  const mod = mods.has('mod');
  return {
    key: key === 'esc' ? 'escape' : key === 'plus' ? '+' : key === 'space' ? ' ' : key,
    ctrl: mods.has('ctrl') || mods.has('control') || (mod && !isMac),
    alt: mods.has('alt') || mods.has('option'),
    shift: mods.has('shift'),
    meta: mods.has('meta') || mods.has('cmd') || (mod && isMac),
  };
}

function matches(e: KeyboardEvent, c: ParsedCombo): boolean {
  if (e.ctrlKey !== c.ctrl || e.altKey !== c.alt || e.metaKey !== c.meta) return false;
  const key = e.key.toLowerCase();
  // '?' and other shifted symbols: shift state is implied by the key itself.
  const isSymbol = c.key.length === 1 && !/[a-z0-9]/.test(c.key);
  if (!isSymbol && e.shiftKey !== c.shift) return false;
  if (key === c.key) return true;
  // Alt+digit / Alt+letter yield different `key` values on some layouts: fall back to the physical key.
  if (/^[a-z]$/.test(c.key) && e.code === `Key${c.key.toUpperCase()}`) return true;
  if (/^[0-9]$/.test(c.key) && e.code === `Digit${c.key}`) return true;
  return false;
}

function isEditable(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

function defaultAllowInInputs(c: ParsedCombo): boolean {
  return /^f\d{1,2}$/.test(c.key) || c.ctrl || c.alt || c.meta;
}

/* ---------- registry (drives the help overlay) ---------- */

export interface RegisteredShortcut {
  id: number;
  keys: string;
  description: string;
  group: string;
  /** Registered without a description: active but not listed in the help overlay. */
  hidden?: boolean;
}

let nextId = 1;
let registry: RegisteredShortcut[] = [];
const listeners = new Set<() => void>();
function emit() {
  listeners.forEach((l) => l());
}
function register(items: Omit<RegisteredShortcut, 'id'>[]): () => void {
  const added = items.map((i) => ({ ...i, id: nextId++ }));
  registry = [...registry, ...added];
  emit();
  return () => {
    const ids = new Set(added.map((a) => a.id));
    registry = registry.filter((r) => !ids.has(r.id));
    emit();
  };
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** Described shortcuts currently active (what the help overlay lists). */
export function useRegisteredShortcuts(): RegisteredShortcut[] {
  const all = useSyncExternalStore(
    subscribe,
    () => registry,
    () => registry,
  );
  return useMemo(() => all.filter((r) => !r.hidden), [all]);
}

/** Every active binding, including hidden aliases. */
export function getActiveShortcuts(): RegisteredShortcut[] {
  return registry;
}

/** Canonical form of a combo ("Alt+Shift+1" == "shift+alt+1") for collision detection. */
export function comboSignature(combo: string): string {
  const c = parseCombo(combo);
  return [c.ctrl && 'ctrl', c.alt && 'alt', c.shift && 'shift', c.meta && 'meta', c.key]
    .filter(Boolean)
    .join('+');
}

/** Groups of active bindings that share the same combo (a bug: only the first would ever fire). */
export function findShortcutCollisions(
  shortcuts: RegisteredShortcut[] = registry,
): RegisteredShortcut[][] {
  const map = new Map<string, RegisteredShortcut[]>();
  for (const s of shortcuts) {
    const sig = comboSignature(s.keys);
    map.set(sig, [...(map.get(sig) ?? []), s]);
  }
  return [...map.values()].filter((g) => g.length > 1);
}

/** "alt+1" -> "Alt+1", "ctrl+enter" -> "Ctrl+Enter" for display (see <Kbd>). */
export function formatCombo(combo: string): string {
  return combo
    .split('+')
    .map((p) => {
      const t = p.trim().toLowerCase();
      if (t === 'mod') return isMac ? '⌘' : 'Ctrl';
      if (t === 'escape' || t === 'esc') return 'Esc';
      if (/^f\d+$/.test(t)) return t.toUpperCase();
      return t.length === 1 ? t.toUpperCase() : t.charAt(0).toUpperCase() + t.slice(1);
    })
    .join('+');
}

export function useHotkeys(bindings: HotkeyBinding[], options: HotkeyOptions = {}): void {
  const { enabled = true } = options;
  const ref = useRef(bindings);
  useEffect(() => {
    ref.current = bindings;
  });

  // Registry entries only change when the keys/descriptions change, not on every handler identity change.
  const signature = bindings
    .map((b) => `${b.keys}|${b.description ?? ''}|${b.group ?? ''}`)
    .join(';;');

  useEffect(() => {
    if (!enabled) return;
    const items = ref.current.map((b) => ({
      keys: b.keys,
      description: b.description ?? '',
      group: b.group ?? 'General',
      hidden: !b.description,
    }));
    return register(items);
  }, [enabled, signature]);

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      for (const b of ref.current) {
        const combo = parseCombo(b.keys);
        if (!matches(e, combo)) continue;
        if (isEditable(e.target) && !(b.allowInInputs ?? defaultAllowInInputs(combo))) continue;
        if (b.preventDefault !== false) e.preventDefault();
        b.handler(e);
        return;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
